// Australia/Melbourne date helpers + Google Calendar link builder.
const MEL_TZ = "Australia/Melbourne";

export const fmtDate = (iso, opts = {}) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: MEL_TZ,
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...opts,
    }).format(new Date(iso));
  } catch { return iso; }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: MEL_TZ,
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    }).format(new Date(iso));
  } catch { return iso; }
};

// Milestone dates: "YYYY-MM-DD" is already a Melbourne calendar date; timestamps convert to Melbourne.
export const fmtMelbDay = (value) => {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Intl.DateTimeFormat("en-AU", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric" })
      .format(new Date(Date.UTC(y, m - 1, d)));
  }
  return fmtDate(value);
};

export const melbourneMonthKey = (d = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: MEL_TZ, year: "numeric", month: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  return `${y}-${m}`;
};

export const shiftMonth = (key, delta) => {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

export const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric" })
    .format(new Date(Date.UTC(y, m - 1, 1)));
};

// Convert a local <input type="datetime-local"> value to ISO with Melbourne offset.
// The datetime-local is naive-local (user's device tz). We treat it as Melbourne time.
export const localInputToMelbourneISO = (val) => {
  if (!val) return null;
  // val = "YYYY-MM-DDTHH:mm"
  // Build a Date treating the components as Melbourne wall-clock, then produce ISO.
  const [datePart, timePart] = val.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  // Determine Melbourne offset for that instant by iterating: start with UTC guess, adjust
  // Simpler: create Date from parts as UTC, then compute Melbourne offset for that moment.
  const guess = new Date(Date.UTC(y, mo - 1, d, h, mi));
  const offsetMin = melbourneOffsetMinutes(guess);
  const utc = new Date(guess.getTime() - offsetMin * 60000);
  return utc.toISOString();
};

// Convert ISO -> "YYYY-MM-DDTHH:mm" for a datetime-local input, interpreted as Melbourne wall time.
export const isoToLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: MEL_TZ, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(d);
    const g = (t) => parts.find((p) => p.type === t).value;
    return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
  } catch { return ""; }
};

function melbourneOffsetMinutes(date) {
  // Compute offset in minutes for Melbourne at given instant.
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: MEL_TZ, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const parts = dtf.formatToParts(date);
  const g = (t) => parts.find((p) => p.type === t).value;
  const asUTC = Date.UTC(+g("year"), +g("month") - 1, +g("day"),
                         +g("hour"), +g("minute"), +g("second"));
  return (asUTC - date.getTime()) / 60000;
}

// Build a Google Calendar "create event" link
export const buildGCalLink = ({ title, details, location, startISO, durationMinutes = 60 }) => {
  if (!startISO) return "#";
  const start = new Date(startISO);
  const end = new Date(start.getTime() + durationMinutes * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "PBO Meeting",
    dates: `${fmt(start)}/${fmt(end)}`,
    details: details || "",
    location: location || "",
    ctz: MEL_TZ,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
